import {makeStyles, Typography} from "@material-ui/core";
import Tr from "translations/Translations";
import PropTypes from "prop-types";
import GridBlock from "components/GridBlock";
import Button from "components/Button";
import React, {useCallback, useEffect, useRef, useState} from "react";
import moment from "libs/moment";
import {formatDaysHoursMinutesSeconds, getDaysHoursMinutesSeconds} from "libs/durations";
import {AdditionalRoundStatus} from "constants/AdditionalRoundStatus";
import OfferChoiceEditAdditionalRoundsDatesPopup from "./OfferChoiceEditAdditionalRoundsDatesPopup";
import AdditionalRoundService from "services/AdditionalRoundService";
import ProgressStatus from "constants/ProgressStatus";
import ProgressBar from "components/ProgressBar";
import BoldTypography from "components/BoldTypography";
import {DemandValidation} from "constants/DemandValidation";

const useStyles = makeStyles(theme => ({
    buttons: {
        marginTop: theme.spacing(2)
    },
    button: {
        marginLeft: theme.spacing()
    }
}));

const countTimeFunction = (endTime, serverBrowserTimeDiffSeconds, setLeftTable, timerInterval, endTimeCallback) => {
    const end = moment(endTime);
    const nowBrowserTime = moment();
    const secondsDiff = end.diff(nowBrowserTime, "s") - serverBrowserTimeDiffSeconds.current;
    if (secondsDiff <= 0) {
        if (timerInterval.current)
            clearInterval(timerInterval.current);
        endTimeCallback();
    }
    setLeftTable(getDaysHoursMinutesSeconds(secondsDiff, null));
}

//to widzimy tylko w statusie OFFER_COLLECTION i OFFER_DECRYPTION
function OfferChoiceSummaryCellAdditionalRound(
    {
        demandId,
        editable,
        working,
        rowWorking,
        setRowWorking,
        refreshRow,
        serverBrowserTimeDiff,
        roundName,
        additionalRoundId,
        additionalRoundStatus,
        additionalRoundEndDate,
        additionalRoundOpenOfferDate,

    }) {
    const classes = useStyles();

    const [leftTableToOfferEnd, setLeftTableToOfferEnd] = useState(null);
    const [leftTableToOpenOffer, setLeftTableToOpenOffer] = useState(null);
    const [editDatesOpened, setEditDatesOpened] = useState(false);
    const [decryptionStatus, setDecryptionStatus] = useState(null);
    const [decryptionProgress, setDecryptionProgress] = useState([-1, 0]);
    const decryptionInterval = useRef(0);
    const timerInterval = useRef(0);
    const decryptionFinished = useRef(false);

    const endTimeCallback = useCallback(() => {
        setTimeout(refreshRow, 3000);
    }, [refreshRow]);

    useEffect(() => {
        setDecryptionStatus(null);


        if (additionalRoundStatus === AdditionalRoundStatus.OFFER_COLLECTION) {
            const nowBrowserTime = moment();
            const end = moment(additionalRoundEndDate);
            const secondsLeftToOfferEnd = moment(end).diff(nowBrowserTime, 's') - serverBrowserTimeDiff.current;
            setLeftTableToOfferEnd(getDaysHoursMinutesSeconds(secondsLeftToOfferEnd, null));
            //trwa zbieranie
            if (secondsLeftToOfferEnd > 0) {
                timerInterval.current = setInterval(countTimeFunction, 250,
                    additionalRoundEndDate,
                    serverBrowserTimeDiff,
                    setLeftTableToOfferEnd,
                    timerInterval,
                    endTimeCallback);
                //zakończono zbieranie
            } else {
                const openOffer = moment(additionalRoundOpenOfferDate);
                const secondsLeftToOpenOffer = moment(openOffer).diff(nowBrowserTime, 's') - serverBrowserTimeDiff.current;
                //minął termin otwarcia ofert
                if (secondsLeftToOpenOffer > 0) {
                    timerInterval.current = setInterval(countTimeFunction, 250,
                        additionalRoundOpenOfferDate,
                        serverBrowserTimeDiff,
                        setLeftTableToOpenOffer,
                        timerInterval,
                        endTimeCallback);
                } else {
                    AdditionalRoundService.getOffersDecryptionStatus(additionalRoundId)
                        .then(resp => setDecryptionStatus(resp));
                }
            }
        } else if (additionalRoundStatus === AdditionalRoundStatus.OFFER_DECRYPTION) {
            AdditionalRoundService.getOffersDecryptionStatus(additionalRoundId).then((resp) => {
                setDecryptionStatus(resp);
            })
        }

        return () => {
            if (timerInterval.current) {
                clearInterval(timerInterval.current);
                timerInterval.current = 0;
            }
            if (decryptionInterval.current) {
                clearInterval(decryptionInterval.current);
                decryptionInterval.current = 0;

            }
        }
    }, [additionalRoundEndDate, additionalRoundId, additionalRoundOpenOfferDate, additionalRoundStatus, endTimeCallback, serverBrowserTimeDiff]);

    useEffect(() => {
        if (decryptionStatus && decryptionStatus.status === ProgressStatus.IN_PROGRESS && !decryptionInterval.current) {
            decryptionInterval.current = setInterval(async () => {
                const resp = await AdditionalRoundService.getOffersDecryptionProgress(additionalRoundId);
                setDecryptionProgress(resp.result);
                if (resp.result[0] === 100) {
                    clearInterval(decryptionInterval.current);
                    decryptionInterval.current = setInterval(async () => {
                        const resp = await AdditionalRoundService.getOffersDecryptionStatus(additionalRoundId);
                        setDecryptionStatus(resp);
                        if (resp.status === ProgressStatus.PROCESSED) {
                            clearInterval(decryptionInterval.current);
                            decryptionInterval.current = 0;
                        }
                    }, 2000)
                }
            }, 3000);
        } else if (decryptionStatus && decryptionStatus.status === ProgressStatus.PROCESSED && !decryptionFinished.current) {
            refreshRow([DemandValidation.DOCUMENTS, DemandValidation.CONTRACTOR_VERIFICATION, DemandValidation.OFFERS_EVALUATION, DemandValidation.OFFER_CHOICE]);
            decryptionFinished.current = true;
        }
        // eslint-disable-next-line
    }, [decryptionStatus]);


    const onDecryptionStart = () => {
        setRowWorking(true);
        AdditionalRoundService.startOffersDecryptionProcess(additionalRoundId)
            .finally(refreshRow);
    };

    return (<GridBlock
        column
        justify='space-between'
        alignItems='center'
    >
        {
            additionalRoundStatus === AdditionalRoundStatus.OFFER_COLLECTION &&
            <>
                {leftTableToOfferEnd !== null ?
                    <>
                        <Typography>{Tr('additional_offer_collection_pending')}</Typography>
                        <Typography>{Tr('additional_offer_collection_left')}: <BoldTypography
                            component="span">{formatDaysHoursMinutesSeconds(leftTableToOfferEnd)}</BoldTypography></Typography>
                    </> : <>
                        <Typography>{Tr('additional_offer_collection_finished')}</Typography>
                        {leftTableToOpenOffer !== null &&
                        <Typography>{Tr('additional_offer_open_time_left')}: <BoldTypography
                            component="span">{formatDaysHoursMinutesSeconds(leftTableToOpenOffer)}</BoldTypography></Typography>
                        }
                    </>
                }
            </>
        }
        {
            additionalRoundStatus === AdditionalRoundStatus.OFFER_DECRYPTION &&
            <>
                <Typography>{Tr('additional_offer_decryption')}</Typography>
                {decryptionProgress[0] <= 0 && decryptionProgress[1] > 0 && <Typography>{decryptionProgress[1] + ' ' + Tr('in_queue')}</Typography>}
                {(decryptionProgress[0] > 0 || decryptionProgress[1] === 0) && <ProgressBar max={100} value={decryptionProgress[0]}/>}
            </>
        }

        <GridBlock className={classes.buttons}>
            {additionalRoundStatus === AdditionalRoundStatus.OFFER_COLLECTION &&
            <>
                <Button disabled={rowWorking || working || !editable}
                        variant={leftTableToOfferEnd !== null ? 'contained' : 'outlined'}
                        onClick={() => setEditDatesOpened(true)}>{Tr('edit_dates')}
                </Button>
                {leftTableToOfferEnd === null &&
                <Button
                    className={classes.button}
                    disabled={(decryptionStatus === null || decryptionStatus.status !== ProgressStatus.CAN_START) || rowWorking || working || !editable}
                    onClick={onDecryptionStart}>{Tr('open_offers')}
                </Button>
                }
            </>
            }
        </GridBlock>

        {editDatesOpened &&
        <OfferChoiceEditAdditionalRoundsDatesPopup
            demandId={demandId}
            roundName={roundName}
            additionalRoundId={additionalRoundId}
            additionalRoundStatus={additionalRoundStatus}
            additionalRoundEndDate={additionalRoundEndDate}
            additionalRoundOpenOfferDate={additionalRoundOpenOfferDate}
            onCancel={() => setEditDatesOpened(false)}
            onSuccess={() => {
                refreshRow().then(() => setEditDatesOpened(false));
            }}
        />}
    </GridBlock>)
}

OfferChoiceSummaryCellAdditionalRound.propTypes = {
    demandId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    editable: PropTypes.bool.isRequired,
    working: PropTypes.bool.isRequired,
    rowWorking: PropTypes.bool.isRequired,
    roundName: PropTypes.string.isRequired,
    additionalRoundId: PropTypes.number.isRequired,
    additionalRoundStatus: PropTypes.string.isRequired,
    additionalRoundEndDate: PropTypes.string.isRequired,
    additionalRoundOpenOfferDate: PropTypes.string.isRequired,
    refreshRow: PropTypes.func.isRequired,
    setRowWorking: PropTypes.func.isRequired,
    serverBrowserTimeDiff: PropTypes.object.isRequired,
}

export default OfferChoiceSummaryCellAdditionalRound;

